import { getApiClient, type RequestOpts } from './api-client';
import { KNOWLEDGE_SERVICE_URL as servicePath } from '../constants/APIconstants';
import type { KnowledgeContentType } from '../constants/tags.constants';
import { normalizeRevisionLog } from '../utils/knowledge.utils';

/** The backend caps `GET /items?ids=` at 200 ids per request. */
export const ITEMS_BY_IDS_MAX = 200;

/**
 * The "item row" shape every on-demand list endpoint returns (the backend's
 * `LIST` projection): no content, no binder links.
 */
export interface KnowledgeItemRow {
  id: string;
  workspaceId?: string;
  ownerIds?: string[];
  title?: string;
  subtitle?: string;
  type?: string;
  link?: string;
  thumbnail?: string;
  expiresAt?: string | null;
  updatedAt?: string;
  tags?: {
    specialty?: string | null;
    subspecialty?: string | null;
    contentType?: KnowledgeContentType | null;
  };
  labels?: string[];
  keywords?: string[];
  attachmentsCount?: number;
  options?: { externalSyncProvider?: string | null; [key: string]: unknown };
}

/** The full item (`GET /items/:id`): the row plus content, keywords and the rest. */
export interface KnowledgeItem extends KnowledgeItemRow {
  content?: string;
  keywords?: string[];
  contributors?: unknown[];
  categoriesInfo?: { categoryId: string; workspaceId?: string }[];
  fileUpdatedAt?: string | null;
  lastUpdatedDate?: string;
  updatedBy?: unknown;
  revisionLog?: { updatedAt: string; userId?: string; email?: string }[];
  metadata?: unknown;
  attachments?: unknown[];
  captionsUrl?: string;
  hubInfo?: unknown;
}

/**
 * Splits ids into request-sized chunks, in order.
 *
 * @param ids - The ids to split.
 * @param size - Ids per chunk. Default `ITEMS_BY_IDS_MAX` (the backend cap).
 * @returns The chunks; none for no ids.
 * @example
 * chunkIds(ids).map(chunk => knowledgeRqApi.listItems(chunk));
 */
export function chunkIds(ids: string[], size = ITEMS_BY_IDS_MAX): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

/**
 * Puts rows in the order their ids were asked, whatever order the server used.
 *
 * @param rows - Rows with an `id`.
 * @param ids - The ids as requested.
 * @returns A new array; an id missing from `rows` is simply absent.
 * @example
 * sortByRequestedIds([{ id: 'b' }, { id: 'a' }], ['a', 'b']); // [{ id: 'a' }, { id: 'b' }]
 */
export function sortByRequestedIds<T extends { id: string }>(rows: T[], ids: string[]): T[] {
  const position = new Map(ids.map((id, index) => [id, index]));
  return [...rows].sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0));
}

/**
 * A binder entry: the row plus how the binder holds it. `linkOwnerIds` is set on links only.
 */
export interface CategoryItemRow extends KnowledgeItemRow {
  linked: boolean;
  isSourceDerived: boolean;
  linkOwnerIds?: string[];
}

/**
 * The review data: items due for review by owner id (`unassigned` for none) and
 * `reviewableCount`, the size of the whole review cycle.
 */
export interface ReviewItems {
  groups: Record<string, KnowledgeItemRow[]>;
  reviewableCount: number;
}

/**
 * A contributor named on an item who is not a user.
 */
export interface ExternalContributor {
  contributorId: string;
  displayName: string;
}

// Knowledge endpoints on the react-query api-client; the legacy Executer-based
// endpoints live in knowledge.service.api.js.
class KnowledgeRqService {
  // The revision log comes oldest-first; the UI reads `revisionLog[0]` as the last edit.
  async getItem(id: string, { signal }: RequestOpts = {}): Promise<KnowledgeItem> {
    const item = await getApiClient().get<KnowledgeItem>(`${servicePath}items/${id}`, { signal });
    return normalizeRevisionLog(item) as KnowledgeItem;
  }

  /**
   * Rows for the given ids, in the order asked. Ids the caller cannot see are
   * left out, not errored. More than the cap goes out as several requests.
   */
  async listItems(ids: string[], { signal }: RequestOpts = {}): Promise<KnowledgeItemRow[]> {
    const pages = await Promise.all(
      chunkIds(ids).map(chunk =>
        getApiClient().get<KnowledgeItemRow[]>(`${servicePath}items`, {
          params: { ids: chunk.join(',') },
          signal,
        }),
      ),
    );
    return sortByRequestedIds(pages.flat(), ids);
  }

  /**
   * The rows of one binder, in binder order; rows the caller cannot see are left out.
   *
   * @param categoryId - The binder id.
   * @param options - The abort signal.
   * @returns The rows with their link flags.
   * @example
   * const rows = await knowledgeRqApi.listCategoryItems(binderId, { signal });
   */
  listCategoryItems(categoryId: string, { signal }: RequestOpts = {}): Promise<CategoryItemRow[]> {
    return getApiClient().get<CategoryItemRow[]>(`${servicePath}categories/${categoryId}/items`, {
      signal,
    });
  }

  /**
   * Items expired or due within the review window, grouped by owner.
   *
   * @param ownerIds - Keep only these owners' groups; empty for all.
   * @param options - The abort signal.
   * @returns The groups and the review-cycle size.
   * @example
   * const { groups } = await knowledgeRqApi.listReviewItems([userId]);
   */
  listReviewItems(ownerIds: string[] = [], { signal }: RequestOpts = {}): Promise<ReviewItems> {
    return getApiClient().get<ReviewItems>(`${servicePath}items/review`, {
      params: ownerIds.length > 0 ? { ownerIds: ownerIds.join(',') } : undefined,
      signal,
    });
  }

  /**
   * The org's external contributors, sorted by name.
   *
   * @param options - The abort signal.
   * @returns One entry per contributor.
   * @example
   * const contributors = await knowledgeRqApi.listContributors({ signal });
   */
  listContributors({ signal }: RequestOpts = {}): Promise<ExternalContributor[]> {
    return getApiClient().get<ExternalContributor[]>(`${servicePath}contributors`, { signal });
  }
}

export default new KnowledgeRqService();
